import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Circle,
  Sun,
  Zap,
  Star,
  Waves
} from 'lucide-react';

/**
 * Visual effects configuration for video templates
 * Matches the VisualEffectsConfig type in backend/remotion-renderer
 */
const DEFAULT_EFFECTS = {
  sparkles: true,
  floatingShapes: true,
  bokeh: true,
  ambientGlow: true,
  priceShimmer: true,
  pulsingGlow: false,
};

/**
 * Effect definitions with metadata
 */
const EFFECT_DEFINITIONS = [
  {
    id: 'sparkles',
    icon: Sparkles,
    labelKey: 'effects.sparkles',
    descriptionKey: 'effects.sparklesDesc',
    defaultLabel: 'Funkeln',
    defaultDescription: 'Funkelnde Partikel um das Produkt',
  },
  {
    id: 'floatingShapes',
    icon: Circle,
    labelKey: 'effects.floatingShapes',
    descriptionKey: 'effects.floatingShapesDesc',
    defaultLabel: 'Schwebende Formen',
    defaultDescription: 'Geometrische Formen im Hintergrund',
  },
  {
    id: 'bokeh',
    icon: Sun,
    labelKey: 'effects.bokeh',
    descriptionKey: 'effects.bokehDesc',
    defaultLabel: 'Bokeh',
    defaultDescription: 'Weiche Lichtpunkte',
  },
  {
    id: 'ambientGlow',
    icon: Waves,
    labelKey: 'effects.ambientGlow',
    descriptionKey: 'effects.ambientGlowDesc',
    defaultLabel: 'Ambiente Beleuchtung',
    defaultDescription: 'Sanftes Licht von oben',
  },
  {
    id: 'priceShimmer',
    icon: Zap,
    labelKey: 'effects.priceShimmer',
    descriptionKey: 'effects.priceShimmerDesc',
    defaultLabel: 'Preis-Glanz',
    defaultDescription: 'Shimmer-Effekt über dem Preis',
  },
  {
    id: 'pulsingGlow',
    icon: Star,
    labelKey: 'effects.pulsingGlow',
    descriptionKey: 'effects.pulsingGlowDesc',
    defaultLabel: 'Pulsierender Rand',
    defaultDescription: 'Leuchtender Glow am Rand',
  },
];

/**
 * VisualEffectsPanel Component
 *
 * Panel with toggle switches for visual effects in video templates.
 * Effects can be suggested by LLM and toggled by users.
 */
export const VisualEffectsPanel = ({
  effects = DEFAULT_EFFECTS,
  onChange,
  llmSuggested = null,
  disabled = false,
}) => {
  const { t } = useTranslation('approval');

  /**
   * Handle effect toggle
   */
  const handleToggle = (effectId) => {
    if (disabled) return;

    const newEffects = {
      ...effects,
      [effectId]: !effects[effectId],
    };

    if (onChange) {
      onChange(newEffects);
    }
  };

  /**
   * Apply LLM suggestions
   */
  const applyLLMSuggestions = () => {
    if (llmSuggested && onChange) {
      onChange(llmSuggested);
    }
  };

  /**
   * Reset to defaults
   */
  const resetToDefaults = () => {
    if (onChange) {
      onChange(DEFAULT_EFFECTS);
    }
  };

  return (
    <div className="visual-effects-panel">
      {/* Header with actions */}
      <div className="effects-header">
        <h4 className="effects-title">
          <Sparkles size={16} />
          {t('effects.title', 'Visuelle Effekte')}
        </h4>

        <div className="effects-actions">
          {llmSuggested && (
            <button
              className="effects-action-btn llm-suggestion"
              onClick={applyLLMSuggestions}
              title={t('effects.applyLLMSuggestion', 'KI-Empfehlung anwenden')}
            >
              <Sparkles size={14} />
              KI
            </button>
          )}
          <button
            className="effects-action-btn reset"
            onClick={resetToDefaults}
            title={t('effects.reset', 'Zurücksetzen')}
          >
            Reset
          </button>
        </div>
      </div>

      {/* LLM Reasoning */}
      {llmSuggested?.reasoning && (
        <div className="llm-reasoning">
          <small>{llmSuggested.reasoning}</small>
        </div>
      )}

      {/* Effect toggles */}
      <div className="effects-grid">
        {EFFECT_DEFINITIONS.map((effect) => {
          const Icon = effect.icon;
          const isEnabled = effects[effect.id] ?? DEFAULT_EFFECTS[effect.id];
          const isLLMRecommended = llmSuggested?.[effect.id] === true;

          return (
            <div
              key={effect.id}
              className={`effect-toggle ${isEnabled ? 'enabled' : ''} ${isLLMRecommended ? 'llm-recommended' : ''} ${disabled ? 'disabled' : ''}`}
              onClick={() => handleToggle(effect.id)}
              role="switch"
              aria-checked={isEnabled}
              tabIndex={disabled ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle(effect.id);
                }
              }}
            >
              <div className="effect-icon">
                <Icon size={20} />
              </div>
              <div className="effect-info">
                <span className="effect-label">
                  {t(effect.labelKey, effect.defaultLabel)}
                </span>
                <span className="effect-description">
                  {t(effect.descriptionKey, effect.defaultDescription)}
                </span>
              </div>
              <div className="effect-switch">
                <div className={`switch-track ${isEnabled ? 'on' : 'off'}`}>
                  <div className="switch-thumb" />
                </div>
              </div>
              {isLLMRecommended && (
                <div className="llm-badge" title="KI empfohlen">
                  <Sparkles size={10} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        .visual-effects-panel {
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .effects-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .effects-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #111827;
        }

        .effects-actions {
          display: flex;
          gap: 8px;
        }

        .effects-action-btn {
          padding: 4px 8px;
          font-size: 11px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .effects-action-btn.llm-suggestion {
          background: linear-gradient(135deg, #8B5CF6, #D946EF);
          color: white;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .effects-action-btn.llm-suggestion:hover {
          transform: scale(1.05);
        }

        .effects-action-btn.reset {
          background: #e5e7eb;
          color: #6b7280;
        }

        .effects-action-btn.reset:hover {
          background: #d1d5db;
        }

        .llm-reasoning {
          padding: 8px;
          margin-bottom: 12px;
          background: rgba(139, 92, 246, 0.1);
          border-radius: 4px;
          border-left: 3px solid #8B5CF6;
        }

        .llm-reasoning small {
          color: #6b7280;
          font-size: 11px;
          font-style: italic;
        }

        .effects-grid {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .effect-toggle {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          user-select: none;
        }

        .effect-toggle:hover:not(.disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .effect-toggle.enabled {
          background: rgba(139, 92, 246, 0.08);
          border: 1px solid rgba(139, 92, 246, 0.3);
        }

        .effect-toggle.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .effect-toggle.llm-recommended {
          box-shadow: 0 0 0 1px rgba(139, 92, 246, 0.5);
        }

        .effect-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          background: #f3f4f6;
          color: #6b7280;
        }

        .effect-toggle.enabled .effect-icon {
          background: linear-gradient(135deg, #8B5CF6, #D946EF);
          color: white;
        }

        .effect-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .effect-label {
          font-size: 13px;
          font-weight: 500;
          color: #111827;
        }

        .effect-description {
          font-size: 11px;
          color: #6b7280;
        }

        .effect-switch {
          flex-shrink: 0;
        }

        .switch-track {
          width: 36px;
          height: 20px;
          border-radius: 10px;
          background: #d1d5db;
          position: relative;
          transition: background 0.2s;
        }

        .switch-track.on {
          background: linear-gradient(135deg, #8B5CF6, #D946EF);
        }

        .switch-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          position: absolute;
          top: 2px;
          left: 2px;
          transition: transform 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }

        .switch-track.on .switch-thumb {
          transform: translateX(16px);
        }

        .llm-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: linear-gradient(135deg, #8B5CF6, #D946EF);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }
      `}</style>
    </div>
  );
};

VisualEffectsPanel.propTypes = {
  effects: PropTypes.shape({
    sparkles: PropTypes.bool,
    floatingShapes: PropTypes.bool,
    bokeh: PropTypes.bool,
    ambientGlow: PropTypes.bool,
    priceShimmer: PropTypes.bool,
    pulsingGlow: PropTypes.bool,
  }),
  onChange: PropTypes.func,
  llmSuggested: PropTypes.shape({
    sparkles: PropTypes.bool,
    floatingShapes: PropTypes.bool,
    bokeh: PropTypes.bool,
    ambientGlow: PropTypes.bool,
    priceShimmer: PropTypes.bool,
    pulsingGlow: PropTypes.bool,
    reasoning: PropTypes.string,
  }),
  disabled: PropTypes.bool,
};

export default VisualEffectsPanel;
